import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "../DataTable";
import BulkUploadProperties from "../BulkUploadProperties";
import {
    Drawer,
    Button,
    Typography,
    IconButton,
    Card,
    CardBody,
    CardHeader,
    Tooltip
} from "@material-tailwind/react";
import { X, Eye, Trash2, Download } from "lucide-react";
import {
    fetchPropertyData,
} from "../../services/projects";
import { useUser } from "../../context/UserContext";
import "./flatlist.css";
import usePrivilege from "../../hooks/usePrivilege";
import CustomInput from "../CustomInput";
import SearchInput from "../SearchInput";
import { CloseButton, DeleteButton, EditButton } from "../Buttons";
import { useParams } from "react-router-dom";
import ConfirmationModal from "../ConfirmationModal";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import ManualUpload from "../ManualUpload";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../PaginationControls";


function FlatList({ project, phaseId }) {
    const { projectId } = useParams();
    const [searchField, setSearchField] = useState("property_type"); // Default search field
    const { hasPrivilege } = usePrivilege();
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleteButtonVisible, setIsDeleteButtonVisible] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [isManualUploadOpen, setIsManualUploadOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false); // Add loading state for delete operation

    // Create fetch function for usePagination hook
    const fetchPropertiesBackend = useCallback(async ({ page, page_size, search }) => {
        const params = {
            page: page,
            page_size: page_size,
            projectId: projectId,
            customerId: project?.customerId,
            projectPhaseId: phaseId
        };
        
        // Add search parameters if provided
        if (search) {
            params.search = search;
            params.search_field = searchField;
        }
        
        const response = await axiosInstance.get(`/property`, {
            params: params,
        });
        
        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        // The response should already be in paginated format from the backend
        if (response?.data?.items !== undefined && response?.data?.total !== undefined) {
            return response.data;
        } else if (response?.items !== undefined && response?.total !== undefined) {
            return response;
        } else if (Array.isArray(response?.data)) {
            // If it's an array, wrap it in paginated format for backward compatibility
            return {
                items: response.data,
                total: response.data.length,
                page: page,
                page_size: page_size,
                total_pages: Math.ceil(response.data.length / page_size),
                has_next: page * page_size < response.data.length,
                has_previous: page > 1
            };
        } else if (Array.isArray(response)) {
            // If it's an array directly, wrap it in paginated format
            return {
                items: response,
                total: response.length,
                page: page,
                page_size: page_size,
                total_pages: Math.ceil(response.length / page_size),
                has_next: page * page_size < response.length,
                has_previous: page > 1
            };
        }
        return response?.data || response;
    }, [projectId, project?.customerId, phaseId, searchField]);

    // Use pagination hook with manual search mode
    const {
        data: paginationData,
        pagination,
        searchInput,
        loading,
        goToPage,
        changePageSize,
        handleSearchInputChange,
        triggerSearch,
        refetch
    } = usePagination(fetchPropertiesBackend, 20, '', true, true);

    // Extract properties from paginated response
    const filteredProperties = useMemo(() => {
        if (!paginationData) return [];
        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        if (paginationData?.items && Array.isArray(paginationData.items)) {
            return paginationData.items;
        } else if (Array.isArray(paginationData)) {
            return paginationData;
        }
        return [];
    }, [paginationData]);

    // Function to refetch properties
    const refetchProperties = useCallback(async () => {
        try {
            await refetch();
        } catch (error) {
            console.error("Error refetching properties:", error);
            toast.error("Failed to refetch properties.");
        }
    }, [refetch]);

    const toggleManualUploadDialog = () => {
        setIsManualUploadOpen(!isManualUploadOpen);
         setSelectedProperty(null);
    };

    const handleSearchTextChange = (value) => {
        handleSearchInputChange(value);
    };

    const handleSearch = useCallback(async () => {
        await triggerSearch();
    }, [triggerSearch]);

    // Clear search when project/phase changes
    useEffect(() => {
        if (handleSearchInputChange) {
            handleSearchInputChange("");
        }
        if (refetch) {
            refetch();
        }
    }, [projectId, project?.customerId, phaseId]);
    const onSelectionChanged = useCallback((event) => {
        const selectedNodes = event.api.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        setSelectedRows(selectedData);
        setIsDeleteButtonVisible(selectedData.length > 0);
    }, []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        flex: 1,
        minWidth: 100,
        headerClass: "bg-gray-100 text-gray-700",
    }), []);
    const handleUploadSuccess = async () => {
        try {
            await refetch();
        } catch (error) {
            console.error("Error refetching properties after upload:", error);
            toast.error("Failed to refresh properties after upload.");
        }
    };


    // Loading state is handled by DataTable's own `loading` prop further
    // below — no early return is needed here (and an early return would put
    // the hooks declared later in this component after a conditional, which
    // violates rules-of-hooks).

    const handleViewClick = (property) => {
        setSelectedProperty(property);
        setIsDrawerOpen(true);
    };
    const handleEditClick = async (property) => {
        setSelectedProperty(property);
        setIsManualUploadOpen(true);
    };



    const closeDrawer = () => {
        setSelectedProperty(null);
        setIsDrawerOpen(false);
    };


    const handleFileUpload = async () => {
        try {
            await refetch();
        } catch (error) {
            console.error("Error refetching properties:", error);
        }
    };

    const handleDeleteSelected = async () => {

        setDeleteConfirmationOpen(true);
    };
    const handleConfirmDelete = async () => {
        setIsDeleting(true); // Start loading
        try {
            const propertyIdsToDelete = selectedRows.map((row) => row.propertyData.propertyId);

            await axiosInstance.delete(`/property`, {
                data: propertyIdsToDelete,
            });

            await refetch();
            toast.success("Property deleted successfully");
            setSelectedRows([]);
            setIsDeleteButtonVisible(false);
        } catch (error) {
            console.error("Error deleting properties:", error);
            if (error.response && error.response.status === 404) {
                toast.error(error.response.data.detail || "Failed to delete: Property not found");
            } else {
                toast.error("Failed to delete properties. Please try again.");
            }
        } finally {
            setIsDeleting(false); // Stop loading
            setDeleteConfirmationOpen(false);
        }
    };
    const handleCancelDelete = () => {

        setDeleteConfirmationOpen(false);
    };
    // DataTable rows-with-selection — the primitive doesn't have built-in
    // multi-select so we render checkboxes ourselves in the leading column.
    // selectedRows is an array of property objects (kept for compatibility
    // with the existing handleDeleteSelected helper).
    const isRowSelected = useCallback(
        (row) => selectedRows.some((s) => s?.propertyData?.propertyId === row?.propertyData?.propertyId),
        [selectedRows]
    );
    const toggleRowSelection = useCallback((row) => {
        setSelectedRows((prev) => {
            const exists = prev.some((s) => s?.propertyData?.propertyId === row?.propertyData?.propertyId);
            return exists
                ? prev.filter((s) => s?.propertyData?.propertyId !== row?.propertyData?.propertyId)
                : [...prev, row];
        });
    }, []);
    const allSelected =
        filteredProperties.length > 0 &&
        filteredProperties.every((r) => isRowSelected(r));
    const toggleAll = () => {
        if (allSelected) {
            setSelectedRows([]);
        } else {
            setSelectedRows([...filteredProperties]);
        }
    };
    useEffect(() => {
        setIsDeleteButtonVisible(selectedRows.length > 0);
    }, [selectedRows]);

    const sampleHasOwnerCalling = useMemo(
        () => filteredProperties?.[0]?.propertyData?.ownerMobileNoCalling != null,
        [filteredProperties]
    );

    const columns = useMemo(() => {
        const cols = [];
        if (hasPrivilege("canEdit")) {
            cols.push({
                key: "select",
                header: (
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500"
                        aria-label="Select all"
                    />
                ),
                width: 44,
                align: "center",
                render: (row) => (
                    <input
                        type="checkbox"
                        checked={isRowSelected(row)}
                        onChange={(e) => {
                            e.stopPropagation();
                            toggleRowSelection(row);
                        }}
                        className="rounded border-slate-300 text-flamingo-600 focus:ring-flamingo-500"
                        aria-label="Select row"
                    />
                ),
            });
        }
        cols.push(
            { key: "propertyNo", header: "Unit", width: 90, render: (r) => (
                <span className="text-sm font-semibold text-slate-900 tabular-nums">
                    {r?.propertyData?.propertyNo ?? "—"}
                </span>
            )},
            { key: "category", header: "Category", width: 140, render: (r) => (
                <span className="text-sm text-slate-700 truncate block" title={r?.propertyData?.propertyType}>
                    {r?.propertyData?.propertyType || "—"}
                </span>
            )},
            { key: "state", header: "State", width: 130, render: (r) => (
                <span className="text-sm text-slate-700 truncate block" title={r?.propertyData?.propertyState}>
                    {r?.propertyData?.propertyState || "—"}
                </span>
            )},
            { key: "config", header: "Configuration", width: 160, render: (r) => (
                <span className="text-sm text-slate-700 truncate block" title={r?.propertyConfigData?.configName}>
                    {r?.propertyConfigData?.configName || "—"}
                </span>
            )},
            { key: "building", header: "Building", width: 100, render: (r) => (
                <span className="text-sm text-slate-700 tabular-nums">
                    {r?.propertyData?.buildingNo || "—"}
                </span>
            )}
        );
        if (!hasPrivilege("canEdit")) {
            cols.push({
                key: "soldStatus",
                header: "Status",
                width: 110,
                render: (r) => {
                    const v = r?.propertyData?.status;
                    const cls = v === "Sold"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-rose-50 text-rose-700 border-rose-200";
                    return (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${v === "Sold" ? "bg-emerald-500" : "bg-rose-500"}`} />
                            {v || "Unsold"}
                        </span>
                    );
                },
            });
        }
        if (sampleHasOwnerCalling) {
            cols.push({
                key: "ownerCalling",
                header: "Owner phone",
                width: 150,
                render: (r) => (
                    <span className="text-sm text-slate-700 tabular-nums">
                        {r?.propertyData?.ownerMobileNoCalling || "—"}
                    </span>
                ),
            });
        }
        cols.push({
            key: "actions",
            header: "Actions",
            width: hasPrivilege("canEdit") ? 140 : 80,
            align: "center",
            render: (row) => (
                <div className="flex items-center justify-center gap-1">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleViewClick(row); }}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
                        title="View"
                        aria-label="View"
                    >
                        <Eye size={14} />
                    </button>
                    {hasPrivilege("canEdit") && (
                        <Tooltip placement="top">
                            <Tooltip.Trigger>
                                <EditButton onClick={(e) => { e.stopPropagation(); handleEditClick(row); }} />
                            </Tooltip.Trigger>
                            <Tooltip.Content className="bg-slate-800 text-white font-normal text-sm">
                                Edit
                                <Tooltip.Arrow />
                            </Tooltip.Content>
                        </Tooltip>
                    )}
                    {hasPrivilege("canEdit") && (
                        <DeleteButton
                            onClick={(e) => {
                                e.stopPropagation();
                                // Mark just this row as selected, then trigger delete flow.
                                setSelectedRows([row]);
                                handleDeleteSelected();
                            }}
                            className="p-2"
                        />
                    )}
                </div>
            ),
        });
        return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPrivilege, allSelected, sampleHasOwnerCalling, isRowSelected]);


    const rowSelection = "multiple";
    return (
        <div className="p-4 lg:p-6 space-y-4">
            {/* Section header card — matches the card treatment used across
                the rest of the project surfaces. */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 lg:p-5">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h1 className="text-base font-semibold text-slate-900">Properties</h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {pagination?.total ?? filteredProperties.length} propert{(pagination?.total ?? filteredProperties.length) === 1 ? "y" : "ies"}
                            {selectedRows.length > 0 && (
                                <span className="text-flamingo-600 font-medium">
                                    {" "}• {selectedRows.length} selected
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <SearchInput
                            value={searchInput}
                            onChange={handleSearchTextChange}
                            onSearch={handleSearch}
                            placeholder="Search properties..."
                            disabled={loading}
                            className="w-56"
                        />
                        <select
                            value={searchField}
                            onChange={(e) => setSearchField(e.target.value)}
                            className="w-44 px-3 py-2 text-sm border border-slate-300 rounded-md bg-white text-slate-700 focus:outline-none focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 h-10"
                        >
                            <option value="property_type">Property Type</option>
                            <option value="property_sale_type">Sale Type</option>
                        </select>
                        {hasPrivilege("canEdit") && isDeleteButtonVisible && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleDeleteSelected}
                                color="red"
                                className="p-2 bg-rose-500 text-white hover:bg-rose-600 border-none rounded-md"
                            >
                                <Trash2 size={16} />
                                <p className="font-medium text-sm pl-2">Delete</p>
                            </Button>
                        )}
                        {hasPrivilege("canEdit") && (
                            <BulkUploadProperties
                                projectId={projectId}
                                project={project}
                                setProperties={() => refetchProperties()}
                                onUploadSuccess={handleUploadSuccess}
                                onFileUpload={handleFileUpload}
                                phaseId={phaseId}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={deleteConfirmationOpen}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={`Are you sure you want to delete ${selectedRows.length} properties?`}
                isLoading={isDeleting}
            />

            <DataTable
                columns={columns}
                data={filteredProperties}
                loading={loading}
                minWidth={hasPrivilege("canEdit") ? 1000 : 980}
                rowKey={(r, i) => r?.propertyData?.propertyId ?? i}
                emptyState={
                    <div className="px-6 py-12 text-center">
                        <p className="text-base font-semibold text-slate-900">
                            No properties found
                        </p>
                        <p className="text-sm text-slate-500 mt-1">
                            No properties match your current filters or search criteria.
                        </p>
                    </div>
                }
            />
            {pagination?.total > 0 && (
                <div className="mt-4">
                    <PaginationControls
                        pagination={pagination}
                        onPageChange={goToPage}
                        onPageSizeChange={changePageSize}
                        itemName="properties"
                        variant="list"
                    />
                </div>
            )}
            {/* Drawer for viewing property details */}
            <Drawer open={isDrawerOpen} onClose={closeDrawer}>
                <Drawer.Overlay>
                    <Drawer.Panel className="p-6 w-[40%] overflow-y-auto">
                        <Card>
                            <CardHeader className="flex items-center justify-between gap-4">
                                <p className="text-xl font-semibold">Property Details</p>
                                <CloseButton onClick={closeDrawer}>
                                  
                                </CloseButton>
                            </CardHeader>
                            <CardBody className="text-sm">
                                {selectedProperty ? (
                                    <div className="space-y-4">


                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                            <div>
                                                <p className="font-medium">Property Type:</p>
                                                <p>{selectedProperty?.propertyData?.propertyType || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Sale Type:</p>
                                                <p>{selectedProperty?.propertyData?.propertySaleType || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">State:</p>
                                                <p>{selectedProperty?.propertyData?.propertyState || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Building No:</p>
                                                <p>{selectedProperty?.propertyData?.buildingNo || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Property No:</p>
                                                <p>{selectedProperty?.propertyData?.propertyNo || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Status:</p>
                                                <p>{selectedProperty?.propertyData?.status || "N/A"}</p>
                                            </div>
                                            {/* <div>
                                                <p className="font-medium">Property Details:</p>
                                                <p>{selectedProperty?.propertyData?.propertyDetails || "N/A"}</p>
                                            </div> */}
                                        </div>



                                        {/* <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="font-medium">Visiting Time:</p>
                                                <p>{selectedProperty?.propertyData?.visitingStartTime || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Launch Date:</p>
                                                <p>{selectedProperty?.propertyData?.dateLaunched || "N/A"}</p>
                                            </div>
                                        </div> */}
{/* 
                                        <hr className="my-4" /> */}
                                        {/* <p className="text-lg font-medium">Owner Information</p>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <p className="font-medium">Owner Name:</p>
                                                <p>{selectedProperty?.propertyData?.ownerName || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Owner Mobile (WhatsApp):</p>
                                                <p>{selectedProperty?.propertyData?.ownerMobileNoWa || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Owner Mobile (Calling):</p>
                                                <p>{selectedProperty?.propertyData?.ownerMobileNoCalling || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Owner Email:</p>
                                                <p>{selectedProperty?.propertyData?.ownerEmail || "N/A"}</p>
                                            </div>
                                        </div> */}

                                        {/* <hr className="my-4" />
                                        <div className="flex items-center gap-x-2">
                                            <p className="text-lg font-medium">Visiting Hours:</p>
                                            <p>{`${selectedProperty?.propertyData?.visitingStartTime || "N/A"} - ${selectedProperty?.propertyData?.visitingEndTime || "N/A"}`}</p>
                                        </div> */}

                                        <hr className="my-4" />
                                        <p className="text-lg font-medium">Building Information</p>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <p className="font-medium">Building Name:</p>
                                                <p>{selectedProperty?.buildingData?.buildingName || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Number of Floors:</p>
                                                <p>{selectedProperty?.buildingData?.numberOfFloors || "N/A"}</p>
                                            </div>
                                        </div>

                                        <hr className="my-4" />
                                        <p className="text-lg font-medium">Property Metadata</p>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div>
                                                <p className="font-medium">Floor:</p>
                                                <p>{selectedProperty?.metadata?.floor || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Carpet Area (Sqft):</p>
                                                <p>{selectedProperty?.metadata?.carpetAreaSqft || "N/A"}</p>
                                            </div>
                                            <div>
                                                <p className="font-medium">Price:</p>
                                                <p>{selectedProperty?.metadata?.price || "N/A"}</p>
                                            </div>
                                        </div>


                                    </div>
                                ) : (
                                    <div className="text-center text-gray-500">
                                        No property details available.
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Drawer.Panel>
                </Drawer.Overlay>
            </Drawer>

            {/* Manual Upload / Edit Modal */}
            {isManualUploadOpen && (
                <ManualUpload
                    projectId={projectId}
                    phaseId={phaseId}
                    toggleDialog={toggleManualUploadDialog}
                    propertyToEdit={selectedProperty}
                    onSubmit={refetchProperties}
                />
            )}



        </div>
    );
}

export default FlatList;